'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useAuth } from 'src/context/auth';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useSite } from 'src/context/providers/SiteProvider';
import { PulseTextArea, PulseButton, DatePicker } from 'src/components/PulseUI';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import {
  useGetActivityTypesQuery,
  useGetActivityCategoriesQuery,
  useGetCompanySettingsQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
} from 'src/api/redux';
import { COMPANY_SETTINGS_IDS } from 'src/api/redux/settings/constants';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { ActivitySelectOption } from 'src/components/Activity';
import { PortalDropdown } from './components';

interface AddEditActivityProps {
  item?: any;
  onClose?: () => void;
  onCancel?: () => void;
  checkHasData?: boolean;
}

const AddEditActivity = ({
  item = null,
  onClose,
  onCancel,
  checkHasData = false,
}: AddEditActivityProps) => {
  const { user } = useAuth();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { entityDetails } = useProfile();
  const { dispatch } = useSite();

  const { data: activityTypes } = useGetActivityTypesQuery();
  const provider_id = entityDetails?.id;
  const [createActivityMutation] = useCreateActivityMutation();
  const [updateActivityMutation] = useUpdateActivityMutation();

  // Category feature
  const { hasPermission } = useAuthorization();
  const canReadCategories = hasPermission('read.activity_categories.company');
  const { data: companySettings } = useGetCompanySettingsQuery(undefined, {
    skip: !user?.company?.id,
  });
  const enabledSetting = companySettings?.results?.find(
    (s) => s.code === COMPANY_SETTINGS_IDS.ACTIVITY_CATEGORY_ENABLED
  );
  const isCategoryEnabled = enabledSetting?.value === 'true';
  const labelSetting = companySettings?.results?.find(
    (s) => s.code === COMPANY_SETTINGS_IDS.ACTIVITY_CATEGORY_LABEL
  );
  const categoryLabel = labelSetting?.value || 'Category';
  const showCategoryDropdown = isCategoryEnabled && canReadCategories;

  const { data: activityCategories } = useGetActivityCategoriesQuery(
    undefined,
    { skip: !showCategoryDropdown }
  );
  const categoryOptions = useMemo(() => {
    if (!activityCategories?.results?.length) return [];
    return activityCategories.results
      .filter((c) => !c.deactivated)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activityCategories]);

  const [activity, setActivity] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [activityDate, setActivityDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const initialData = useRef({
    activity: activity,
    details: '',
    date: '',
    category: null as number | null,
  });

  const isEditing = !!item;

  const activityData = useMemo(() => {
    if (!activityTypes?.results?.length) return [];
    return activityTypes?.results
      ?.map((item) => ({
        ...item,
        label: item.label,
        value: String(item.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activityTypes]);

  // Map activity types to dropdown options
  const activityTypeOptions = useMemo(
    () =>
      activityData.map((item) => ({
        value: item.value,
        label: item.label,
        renderLabel: <ActivitySelectOption item={item} />,
      })),
    [activityData]
  );

  // Map categories to dropdown options
  const categoryDropdownOptions = useMemo(() => {
    const options = [{ value: null, label: 'None' }];
    categoryOptions.forEach((cat) =>
      options.push({ value: cat.id, label: cat.label })
    );
    return options;
  }, [categoryOptions]);

  const getTextarea = () => textareaRef.current;
  const getDetails = () => getTextarea()?.value || '';

  const [hasContent, setHasContent] = useState(!!item?.details);

  useEffect(() => {
    if (!item) {
      setActivity(activityData?.[0]);
      setSelectedCategory(null);
      const textarea = getTextarea();
      if (textarea) textarea.value = '';
      setHasContent(false);
      const today = new Date().toISOString().split('T')[0];
      setActivityDate(today);
      initialData.current = {
        activity: activityData?.[0],
        details: '',
        date: today,
        category: null,
      };
    } else {
      const activity = activityData?.find(
        (i) => i.value === String(item?.type)
      );
      setActivity(activity);
      setSelectedCategory(item?.category ?? null);
      const textarea = getTextarea();
      if (textarea) textarea.value = item?.details || '';
      setHasContent(!!item?.details);
      const dateValue = item?.activity_date
        ? item.activity_date.split('T')[0]
        : '';
      setActivityDate(dateValue);
      initialData.current = {
        activity,
        details: item?.details || '',
        date: dateValue,
        category: item?.category ?? null,
      };
    }
  }, [activityData, item]);

  // Default to first category when options load in create mode
  const firstCategoryId = categoryOptions?.[0]?.id ?? null;
  useEffect(() => {
    if (!item && firstCategoryId && selectedCategory === null) {
      setSelectedCategory(firstCategoryId);
      initialData.current.category = firstCategoryId;
    }
  }, [firstCategoryId, item, selectedCategory]);

  const setHasDataFlag = (flag) => {
    dispatch({ type: 'SET_HAS_DATA', payload: flag });
  };

  const handleActivitySelect = useCallback(
    (value: string | number | null) => {
      const selected = activityData?.find((i) => i.value === String(value));
      setActivity(selected);
    },
    [activityData]
  );

  const checkIfDataChanged = () => {
    return (
      initialData.current.activity?.value !== activity?.value ||
      initialData.current.details !== getDetails() ||
      initialData.current.date !== activityDate ||
      initialData.current.category !== selectedCategory
    );
  };

  useEffect(() => {
    if (checkHasData) {
      setHasDataFlag(checkIfDataChanged());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, activityDate, selectedCategory]);

  const handleCancel = () => {
    if (!checkHasData) {
      const textarea = getTextarea();
      if (textarea) textarea.value = '';
      setHasContent(false);
      setActivity(
        activityData?.[0] ?? {
          label: 'Note',
          value: 'note',
        }
      );
      setSelectedCategory(null);
      setActivityDate(new Date().toISOString().split('T')[0]);
    }
    onCancel && onCancel();
  };

  const onSubmit = async () => {
    const details = getDetails();
    setIsLoading(true);
    try {
      if (isEditing) {
        await updateActivityMutation({
          activityId: item.id,
          data: {
            type: activity?.value,
            details,
            date: activityDate || undefined,
            ...(showCategoryDropdown && { category: selectedCategory }),
          },
        }).unwrap();
        log.event('Update Activity', {
          location: 'Profile',
          data: {
            type: activity?.label,
            details,
          },
          user: user,
        });
      } else {
        const response = await createActivityMutation({
          providerId: provider_id,
          data: {
            type: activity?.value,
            details,
            date: activityDate || undefined,
            ...(showCategoryDropdown && { category: selectedCategory }),
          },
        }).unwrap();

        log.event('Add Activity', {
          location: 'Profile',
          data: response,
          user: user,
        });
      }
      setNotification({
        title: 'Success',
        message: isEditing
          ? 'Activity updated successfully'
          : 'Activity added successfully',
        type: 'success',
      });
    } catch (err) {
      log.exception('Failed to save activity', {
        tags: {
          location: 'Profile',
          error: err,
        },
      });
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Failed to save activity',
        type: 'error',
      });
    } finally {
      onClose && onClose();
      setIsLoading(false);
      setHasDataFlag(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-4 w-full p-3 rounded-xl border border-neutral-200 bg-white shadow-lg"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="add-edit-activity"
    >
      <PortalDropdown
        options={activityTypeOptions}
        value={activity?.value ?? null}
        onChange={handleActivitySelect}
        aria-label="Select activity type"
        data-testid="activity-type-dropdown-button"
        listTestId="activity-type-dropdown-list"
        optionTestId={(v) => `activity-type-option-${v}`}
      />
      {showCategoryDropdown && (
        <PortalDropdown
          options={categoryDropdownOptions}
          value={selectedCategory}
          onChange={(v) => setSelectedCategory(v as number | null)}
          aria-label={`Select ${categoryLabel.toLowerCase()}`}
          data-testid="activity-category-dropdown-button"
          listTestId="activity-category-dropdown-list"
          optionTestId={(v) =>
            v === null
              ? 'activity-category-option-none'
              : `activity-category-option-${v}`
          }
        />
      )}
      <DatePicker
        value={activityDate}
        onChange={setActivityDate}
        aria-label="Activity date"
        data-testid="activity-date-input"
      />
      <PulseTextArea
        id="add-edit-activity-textarea"
        ref={textareaRef}
        size="md"
        variant="outlined"
        placeholder={`Type a ${activity?.label?.toLowerCase()}...`}
        defaultValue={item?.details || ''}
        autoFocus
        onMouseDown={(e) => e.stopPropagation()}
        onFocus={(e) => {
          e.stopPropagation();
          e.target.select();
        }}
        onInput={() => setHasContent(!!getDetails()?.length)}
        className="min-h-[10rem] max-h-[20rem] bg-[#F5F7F8]"
        slotProps={{
          textarea: {
            className: 'p-2',
          },
        }}
        resize="vertical"
        aria-label="Activity details"
        data-testid="activity-details-textarea"
        onKeyDown={(e) => {
          if (e.metaKey && e.key === 'Enter') {
            onSubmit();
          }
        }}
      />
      <div className="flex justify-end gap-2">
        <PulseButton
          type="button"
          color="neutral"
          variant="soft"
          onClick={handleCancel}
          disabled={!hasContent}
          data-testid="activity-cancel-button"
        >
          {isEditing ? 'Discard Changes' : 'Cancel'}
        </PulseButton>
        <PulseButton
          type="button"
          variant="solid"
          color="primary"
          startDecorator={isLoading ? faSpinner : undefined}
          onClick={onSubmit}
          disabled={!hasContent || isLoading}
          className="bg-[#2391D9] hover:bg-[#1c7ab8]"
          data-testid="activity-log-button"
        >
          Log
        </PulseButton>
      </div>
    </div>
  );
};

export default AddEditActivity;
